import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@barfacil/barfacil-ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { Check, CirclePlus, Settings, X } from 'lucide-react'
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useFormContext } from 'react-hook-form'

import {
  CreatePriceListBody,
  CreatePriceListResponse,
  FilterStatus,
  PriceListResponse,
} from '@/api/_schemas/management'
import { DeletePriceListDialog } from '@/app/panel/management/price-list/_components/delete-price-list-dialog'
import { PriceListFilterSearch } from '@/app/panel/management/price-list/_components/filter-search'
import { PriceListFilterStatus } from '@/app/panel/management/price-list/_components/filter-status'
import { UpdatePriceListDialog } from '@/app/panel/management/price-list/_components/update-price-list-dialog'
import { usePriceListContext } from '@/app/panel/management/price-list/_contexts/price-list-context'
import { useConfigurePriceListInfinite } from '@/app/panel/management/price-list/_hooks/use-configure-price-list'
import { useCreatePriceList } from '@/app/panel/management/price-list/_hooks/use-create-price-list'
import {
  FormCreatePriceListWithStatus,
  FormCreatePriceListWithStatusSchema,
} from '@/app/panel/management/price-list/_schemas/create-price-list'
import {
  Table,
  TableAlert,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableLoading,
  TableRow,
} from '@/components/ui/table'
import { useFormErrorHandler } from '@/hooks/use-form-error-handler'

type ConfigurePriceListTableProps = {
  priceListInfinite: UseInfiniteQueryResult<
    InfiniteData<PriceListResponse, unknown>,
    Error
  >
}

type NewPriceListProps = {
  isPending: boolean
}

type ConfigurePriceListActionsProps = {
  data: CreatePriceListResponse
}

const ConfigurePriceListActions = ({
  data,
}: ConfigurePriceListActionsProps) => (
  <div className="flex items-center justify-end gap-2">
    <UpdatePriceListDialog data={data} />
    <DeletePriceListDialog data={data} isConfiguration={true} />
  </div>
)

const ConfigureNewPriceListActions = () => {
  const form = useFormContext<FormCreatePriceListWithStatus>()

  return (
    <div className="flex items-center justify-end gap-2 text-sky-500">
      <Button className="size-6" variant="ghost" size="icon" type="button">
        <X size={20} onClick={() => form.reset()} />
      </Button>

      <Button
        className="size-6"
        variant="ghost"
        size="icon"
        type="submit"
        form="new-price-list-form"
      >
        <Check size={20} />
      </Button>
    </div>
  )
}

const NewPriceList = ({ isPending }: NewPriceListProps) => {
  const form = useFormContext<FormCreatePriceListWithStatus>()

  return (
    <TableRow>
      <TableCell className="w-1/2 py-8 max-sm:px-2 sm:pr-2">
        <FormField
          control={form.control}
          name="nome"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  id="new-price-list-input"
                  placeholder="Nome"
                  {...field}
                  disabled={isPending}
                />
              </FormControl>

              <FormMessage className="absolute w-max" />
            </FormItem>
          )}
        />
      </TableCell>

      <TableCell className="w-[30%] px-2 py-8">
        <FormField
          control={form.control}
          name="situacao"
          render={({ field }) => (
            <FormItem className="w-full">
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger
                    className="disabled:pointer-events-auto disabled:cursor-not-allowed disabled:bg-white disabled:opacity-50"
                    disabled={isPending}
                  >
                    <SelectValue placeholder="Situação" />
                  </SelectTrigger>
                </FormControl>

                <SelectContent>
                  <SelectGroup>
                    <SelectItem className="flex gap-1" value="Ativo">
                      Ativo
                    </SelectItem>
                    <SelectItem className="flex gap-1" value="Inativo">
                      Inativo
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>

              <FormMessage className="absolute w-max" />
            </FormItem>
          )}
        />
      </TableCell>

      <TableCell className="w-1/5 py-8 max-sm:px-2 sm:pl-2">
        <ConfigureNewPriceListActions />
      </TableCell>
    </TableRow>
  )
}

const ConfigurePriceListTable = ({
  priceListInfinite,
}: ConfigurePriceListTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([])
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const { formErrors, mutation } = useCreatePriceList('configureInfinite')

  const data = useMemo(
    () =>
      priceListInfinite.data
        ? priceListInfinite.data.pages.flatMap((cur) => cur.data)
        : [],
    [priceListInfinite.data],
  )

  const hasPriceList = useMemo(
    () => !!priceListInfinite.data?.pages[0].meta.total,
    [priceListInfinite.data?.pages],
  )

  const columns: ColumnDef<CreatePriceListResponse>[] = [
    {
      accessorKey: 'nome',
      header: () => <span>NOME</span>,
      cell: ({
        row: {
          original: { nome },
        },
      }) => <span>{nome}</span>,
    },
    {
      accessorKey: 'situacao',
      header: () => <span>SITUAÇÃO</span>,
      cell: ({
        row: {
          original: { situacao },
        },
      }) => <span>{situacao}</span>,
    },
    {
      accessorKey: 'actions',
      header: () => <></>,
      cell: ({ row: { original } }) => (
        <ConfigurePriceListActions data={original} />
      ),
    },
  ]

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  })

  const form = useForm<FormCreatePriceListWithStatus>({
    resolver: zodResolver(FormCreatePriceListWithStatusSchema),
    defaultValues: {
      nome: '',
      situacao: '',
    },
  })

  useFormErrorHandler({
    form,
    formErrors,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
  })

  function handleSubmit<T>(e: FormEvent<T>) {
    e.preventDefault()

    return form.handleSubmit((data) => {
      mutation.mutate(data as CreatePriceListBody, {
        onSuccess: () => form.reset(),
      })
    })()
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && priceListInfinite.hasNextPage) {
          priceListInfinite.fetchNextPage()
        }
      },
      { threshold: 1 },
    )

    const sentinel = sentinelRef.current
    if (sentinel) observer.observe(sentinel)

    return () => {
      if (sentinel) observer.unobserve(sentinel)
    }
  }, [priceListInfinite, priceListInfinite.hasNextPage])

  if (priceListInfinite.isLoading) {
    return <TableLoading rows={5} columns={columns.length} />
  }

  if (priceListInfinite.isError) {
    return (
      <TableAlert>Ocorreu um erro ao buscar as listas de preço.</TableAlert>
    )
  }

  return (
    <Form {...form}>
      <form id="new-price-list-form" onSubmit={handleSubmit} />
      <Table className="border border-gray-200">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    className="h-12 bg-blue-400 text-gray-25"
                    key={header.id}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          <NewPriceList isPending={mutation.isPending} />

          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className="max-sm:px-2">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {!hasPriceList && (
        <div className="mt-4 grid place-content-center">
          <span>NENHUM REGISTRO ENCONTRADO!</span>
        </div>
      )}

      {priceListInfinite.isFetchingNextPage && (
        <Spinner className="mx-auto mt-4" />
      )}

      <div ref={sentinelRef} className="h-1" />
    </Form>
  )
}

export const ConfigurePriceListDialog = () => {
  const [open, setOpen] = useState(false)
  const { configureFilters: filters, setConfigureFilters: setFilters } =
    usePriceListContext()

  const priceListInfinite = useConfigurePriceListInfinite(filters)

  function handleOnOpenChange(open: boolean) {
    if (open) {
      setFilters({
        status: FilterStatus.Ativo,
      })
    }

    setOpen(open)
  }

  function handleNewPriceListFocus() {
    document.getElementById('new-price-list-input')?.focus()
  }

  return (
    <Dialog open={open} onOpenChange={handleOnOpenChange}>
      <DialogTrigger asChild>
        <Button className="max-w-min gap-2" variant="secondary" isCustom>
          <Settings size={16} />
          CONFIGURAR LISTAS
        </Button>
      </DialogTrigger>

      <DialogContent className="w-full max-w-2xl gap-8 overflow-hidden">
        <DialogHeader>
          <DialogTitle className="leading-normal">
            CONFIGURAR LISTAS
          </DialogTitle>
        </DialogHeader>

        <DialogDescription className="hidden" />

        <DialogBody className="flex flex-col">
          <div className="flex flex-1 items-center gap-4">
            <PriceListFilterStatus isFetching={priceListInfinite.isFetching} />

            <PriceListFilterSearch
              setFilters={setFilters}
              isFetching={priceListInfinite.isLoading}
            />
          </div>

          <aside className="mb-1 mt-4 flex flex-1 items-center gap-1">
            <h3 className="line-clamp-2 text-blue-400">ADICIONAR LISTA</h3>

            <Button
              variant="ghost"
              size="icon"
              className="mr-1 size-5 max-sm:hidden"
              onClick={handleNewPriceListFocus}
            >
              <CirclePlus size={20} className="text-sky-500" />
            </Button>
          </aside>

          <div className="h-[20.25rem] overflow-auto">
            <ConfigurePriceListTable priceListInfinite={priceListInfinite} />
          </div>
        </DialogBody>

        <DialogFooter className="mt-auto">
          <DialogClose asChild>
            <Button className="ml-auto w-full sm:w-24" variant="tertiary">
              FECHAR
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
